import _ from 'lodash';
import {
  dropGrades,
  extractSettingAs,
  getToolIds,
  getTotals,
  getUngradedStatus,
  sortStudentsByName,
} from '@macmillan-learning/gradebook-shared';
import * as exceptionsService from '../core/exceptions';
import { filterAssignments } from './filter';
import * as actions from '../redux/constants';
import * as lmsService from '../core/lms';
import * as iClickerService from '../core/reef';
import { getStudentGradeObj } from './student-grade-obj';
import { GRADEBOOK_SORT, GRADING_TYPE, SEARCH_TYPES } from '../utils/constants';
import { isWritingFinalPaper } from '../utils/activity';
import { normalizeString } from '../utils/InstructorGradebookHelpers';
import { getInstructorGradebookSessionPageSize } from '../components/InstructorGradebookContainer/InstructorGradebookV2/instructorGradebookPageSizeSession';
import { DEFAULT_PAGE_SIZE } from '../components/InstructorGradebookContainer/InstructorGradebookV2/components/Gradebook/constants';
import { getUserTimezone } from '../utils/util';

function paginate(items, page = 1, perPage = 10) {
  const offset = (page - 1) * perPage;
  const paginatedItems = items.slice(offset).slice(0, perPage);
  return paginatedItems;
}

export function searchStudents(students, searchTerm = '') {
  const search = normalizeString(searchTerm.toLocaleLowerCase());
  return students.filter((student) => {
    const firstName = student.first_name
      ? student.first_name.toLowerCase()
      : 'unknown';
    const lastName = student.last_name
      ? student.last_name.toLowerCase()
      : 'profile';
    const email = student.email ? student.email.toLowerCase() : 'unknown';
    const fullName = `${firstName} ${lastName}`;
    return normalizeString(fullName).includes(search) || email.includes(search);
  });
}

export function getSortProperties(options) {
  const searchParams = new URLSearchParams(window.location.search);

  // Get sort keys from URL
  const urlSortDirectionsString = searchParams.get('studentSortDirection');
  const urlIconSortDirection = searchParams.get('iconSortDirection');
  const urlSortKey = searchParams.get('studentSortKey') || null;

  // Options should override URL sort keys
  const studentSortDirection =
    options.studentSortDirection ||
    urlSortDirectionsString ||
    GRADEBOOK_SORT.DEFAULT_SORT_DIRECTION;
  const iconSortDirection =
    options.iconSortDirection ||
    urlIconSortDirection ||
    GRADEBOOK_SORT.LODASH_ASCENDING;
  const studentSortKey =
    options.studentSortKey === undefined ? urlSortKey : options.studentSortKey;

  return {
    searchParams,
    studentSortKey,
    studentSortDirection,
    iconSortDirection,
  };
}

/**
 * Sorts a list of students given a set sort options.
 * If there are no sort options this function will look for the sort option in the ULR.
 * @param activeStudents
 * @param options.studentSortKeys - An array of student properties to sort on.
 * @param options.studentSortDirections - An array of lodash options to control the sort order.
 * @returns A copy of activeStudents as a sorted list.
 */
export async function sortStudents(activeStudents, options = {}) {
  const {
    studentSortDirection,
    iconSortDirection,
    searchParams,
    studentSortKey,
  } = getSortProperties(options);
  let sortedStudents;
  if (options.sortByGrade && options.courseId) {
    const sortedStudentIdResponse =
      await lmsService.getStudentIdsSortedByAssignmentGrade(
        options.courseId,
        studentSortKey,
      );
    sortedStudents = _.orderBy(
      activeStudents,
      (s) => _.indexOf(sortedStudentIdResponse.studentIds, s.studentId),
      studentSortDirection,
    );
  } else {
    const parsedStudents = _.map(activeStudents, (student) => {
      student.lastName = student.last_name
        ? student.last_name.toLowerCase()
        : 'Profile';
      student.firstName = student.first_name
        ? student.first_name.toLowerCase()
        : 'Unknown';
      return student;
    });
    sortedStudents = sortStudentsByName(parsedStudents, studentSortDirection);
  }

  // Update URL sort keys
  searchParams.set('studentSortDirection', studentSortDirection);
  searchParams.set('iconSortDirection', iconSortDirection);
  if (studentSortKey) {
    searchParams.set('studentSortKey', studentSortKey);
  } else {
    searchParams.delete('studentSortKey');
  }
  const newRelativePathQuery = `${
    window.location.pathname
  }?${searchParams.toString()}`;
  // TODO: madeechange here from linter
  window.history.pushState(null, '', newRelativePathQuery);

  return sortedStudents;
}

const getFilteredStudents = async (roster, options) => {
  let count;
  let filteredStudents = [];

  const search = _.get(options, 'search');
  const searchType = _.get(options, 'searchType');
  const loadSingleStudentFromId = _.get(options, 'loadSingleStudentFromId');
  const page = _.get(options, 'page', 1);
  const limit = parseInt(_.get(options, 'limit', DEFAULT_PAGE_SIZE), 0);
  const exceptionGroups = _.get(options, 'exceptionGroups', []);
  const exceptionGroupStudents = exceptionGroups.reduce(
    (studentIds, exceptionGroup) => {
      return [...studentIds, ...exceptionGroup.studentIds];
    },
    [],
  );

  let relevantStudents = roster.filter((student) => !student.studentDropDate);
  if (exceptionGroups.length > 0) {
    relevantStudents = relevantStudents.filter((student) =>
      exceptionGroupStudents.includes(student.studentId),
    );
  }
  const sortedStudents = await sortStudents(relevantStudents, options);

  if (
    search &&
    searchType === SEARCH_TYPES.STUDENT &&
    !loadSingleStudentFromId
  ) {
    const searchedStudents = searchStudents(sortedStudents, search);
    filteredStudents = paginate(searchedStudents, page, limit);
    count = searchedStudents.length;
  } else {
    count = relevantStudents.length;
    filteredStudents = paginate(sortedStudents, page, limit);
  }

  const pages = Math.ceil(count / limit);
  return { filteredStudents, count, page, limit, pages };
};

export const formatGradebook = (
  gradebook,
  filteredStudents = [],
  isStudentUser = false,
  instructorHasLinkedIClicker = false,
) => {
  const grades = _.get(gradebook, 'grades', []);
  const assignments = _.get(gradebook, 'assignments', []);
  const unfilteredAssignments = _.get(
    gradebook,
    'unfilteredAssignments',
    assignments,
  );
  const exceptions = _.get(gradebook, 'exceptions', []);
  const categories = _.get(gradebook, 'categories', []);
  const launchLogs = _.get(gradebook, 'launchLogs', []);
  const comments = _.get(gradebook, 'comments', []);
  const iClickerStudentRosterMap = _.get(
    gradebook,
    'iClickerStudentRosterMap',
    {},
  );
  const settings = _.get(gradebook, 'settings', []);
  const gradesToReport = extractSettingAs(settings, 'gradesToReport', (x) =>
    _.toInteger(x.value),
  );

  const formattedStudents = filteredStudents.map((student) => {
    const studentGrades = grades.filter(
      (grade) => grade.user_id === student.studentId,
    );
    const studentLaunchLogs = launchLogs.filter(
      (log) => log.studentId === student.studentId,
    );

    const iClickerStudentRosterContainsStudent =
      !!iClickerStudentRosterMap[student.studentId];
    const isBrokenSyncReefGrades =
      instructorHasLinkedIClicker && !iClickerStudentRosterContainsStudent;

    const unfilteredStudentAssignmentsAndGrades = unfilteredAssignments.map(
      (assignment) => {
        const studentExceptions = _.filter(
          exceptions,
          (ex) =>
            ex.assignment_id === assignment.id &&
            _.includes(_.get(ex, 'user_ids', []), student.studentId),
        );
        const studentAssignmentExceptions = _.filter(
          studentExceptions,
          (ex) => ex.exception_type === 'assignment',
        );
        const studentDueDateExceptions = _.filter(
          studentExceptions,
          (ex) => ex.exception_type === 'due_date',
        );
        const studentDueDateException = _.get(
          studentDueDateExceptions,
          '[0]',
          null,
        );

        const studentGradesObj = getStudentGradeObj(
          assignment,
          studentGrades,
          studentAssignmentExceptions,
          studentDueDateException,
          categories,
          studentLaunchLogs,
          student,
          exceptions,
          isBrokenSyncReefGrades,
          isStudentUser,
          gradesToReport,
        );

        const gradeComment = comments.find(
          (comment) =>
            comment.assignment_id === assignment.id &&
            comment.user_id === student.studentId,
        );

        studentGradesObj.gradeComment = gradeComment
          ? gradeComment.comment
          : null;

        return {
          ...studentGradesObj,
          Exceptions: studentExceptions,
          visibility: assignment.visibility,
          visibility_available_date: assignment.visibility_available_date,
          visibility_until_date: assignment.visibility_until_date,
        };
      },
    );

    const droppedUnfilteredStudentAssignmentsAndGrades = dropGrades({
      categories,
      unfilteredStudentAssignmentsAndGrades,
      student,
      exceptions,
    });
    const filteredAssignmentIds = _.map(assignments, 'id');
    const studentAssignmentsAndGrades = _.filter(
      droppedUnfilteredStudentAssignmentsAndGrades,
      (a) => _.includes(filteredAssignmentIds, a.id),
    );

    return {
      ...student,
      isBrokenSyncReefGrades,
      assignments: studentAssignmentsAndGrades,
      unfilteredAssignments: droppedUnfilteredStudentAssignmentsAndGrades,
    };
  });
  return formattedStudents;
};

const isAForeignKey = (filteredStudents, options) => {
  const { studentSortKey } = getSortProperties(options);
  if (!studentSortKey) return false;

  const [first] = filteredStudents;
  return _.get(first, studentSortKey) === undefined;
};

const isNormalGradedAssignment = (assignment) =>
  assignment &&
  assignment.tool !== getToolIds().manual &&
  (assignment.grading_type === GRADING_TYPE.GRADED ||
    assignment.grading_type === GRADING_TYPE.EXTRA_CREDIT);

const getReducedStudentsAssignment = (
  studentWithAssignments,
  categories,
  categoryWeights,
  settings,
  studentSortKey,
) => {
  const showPercent = extractSettingAs(
    settings,
    'showPercent',
    (x) => x.value !== 'false',
  );

  const reducedInfo = studentWithAssignments.map((student) => {
    const studentInfo = {
      studentId: student.studentId,
      totalUngraded: 0,
      name: student.name,
    };

    const clonedCategories = _.cloneDeep(categories);
    const mapperCategories = _.keyBy(clonedCategories, 'id');

    _.get(student, 'assignments', []).forEach((assignment) => {
      const {
        id,
        studentGrade,
        tool,
        isDropped,
        grading_type: assigmentGradingType,
        gradebook_category_id: gradebookCategoryId,
      } = assignment;

      if (tool === getToolIds().manual) {
        studentInfo[id] = studentGrade;
      } else if (assigmentGradingType === GRADING_TYPE.UNGRADED) {
        const numberValue = Number(
          getUngradedStatus(assignment, settings) === 'completed',
        );
        studentInfo.totalUngraded += numberValue;
        studentInfo[id] = numberValue;
      }

      if (!isDropped && mapperCategories[gradebookCategoryId]) {
        const categoryAssignments = _.get(
          mapperCategories,
          `[${gradebookCategoryId}].assignments`,
          [],
        );
        categoryAssignments.push(assignment);
        mapperCategories[gradebookCategoryId].assignments = categoryAssignments;
      }
    });

    if (mapperCategories[studentSortKey]) {
      const { grade, pointsEarned } = getTotals(
        mapperCategories[studentSortKey].assignments,
        categoryWeights,
        settings,
        categories,
      );
      studentInfo[studentSortKey] = showPercent ? grade : pointsEarned;
    } else {
      // If no recognized sort key, then should be course total
      const { grade, pointsEarned } = getTotals(
        student.assignments,
        categoryWeights,
        settings,
        categories,
      );
      studentInfo.course_total = showPercent ? grade : pointsEarned;
    }

    return studentInfo;
  });
  return reducedInfo;
};

const getSortedStudentsByAssignment = (
  studentWithAssignments,
  categoryWeights,
  settings,
  options,
  categories,
) => {
  const { studentSortKey, studentSortDirection } = getSortProperties(options);
  let currentKey = studentSortKey;
  const reducedInfo = getReducedStudentsAssignment(
    studentWithAssignments,
    categories,
    categoryWeights,
    settings,
    studentSortKey,
  );

  const defaultUngradedCategory =
    _.find(categories, (category) => category.is_default_ungraded) || {};
  // this means is sorting by Category Total
  if (
    `${defaultUngradedCategory.id}` === studentSortKey ||
    studentSortKey === '-1'
  ) {
    currentKey = ['totalUngraded'];
  }
  return _.orderBy(reducedInfo, currentKey, studentSortDirection);
};

const assignDefaultCategoryOrder = (categories) => {
  const clone = _.cloneDeep(categories);
  const categoriesOrder = clone
    .filter((category) => category.category_order)
    .map((cat) => cat.category_order);
  const defaultCategory = clone.find((category) => category.is_default);
  const ungradedCategory = clone.find(
    (category) => category.is_default_ungraded,
  );
  // show ever is possible the default category first
  if (
    defaultCategory &&
    !categoriesOrder.includes(1) &&
    defaultCategory.category_order === null
  ) {
    defaultCategory.category_order = 1;
    categoriesOrder.push(1);
  }
  // show ever is possible the the ungraded category in the last
  if (
    ungradedCategory &&
    !categoriesOrder.includes(categories.length) &&
    ungradedCategory.category_order === null
  ) {
    ungradedCategory.category_order = categories.length;
    categoriesOrder.push(categories.length);
  }

  const getIndexAvailable = () => {
    for (let i = 1; i <= categories.length; i++) {
      if (!categoriesOrder.includes(i)) {
        categoriesOrder.push(i);
        return i;
      }
    }
    return categories.length + 1; // default to prevent strange behaviors
  };

  return _.sortBy(
    clone.map((category) => ({
      ...category,
      category_order: !_.isNil(category.category_order)
        ? category.category_order
        : getIndexAvailable(),
    })),
    'category_order',
  );
};

export const getGradebook =
  (courseId, options) => async (dispatch, getState) => {
    // eslint-disable-next-line no-param-reassign
    options = options || {};
    try {
      let roster = [];

      // isNextPage can be passed in as an option
      // if we want to append the fetched students
      // onto the existing. Otherwise, the entire array
      // will be overwritten with the results
      const isNextPage = options.isNextPage;
      if (isNextPage) delete options.isNextPage;

      const forceInitialFetchLoadingIndicator =
        options.forceInitialFetchLoadingIndicator;

      const {
        gradebook: {
          isGradebookFetching,
          searchPhrase,
          searchType,
          exceptionGroups,
          filterTypes,
          searchDateFrom,
          searchDateTo,
          page: currentPage,
        },
      } = getState();

      // Checking isGradebookFetching to avoid double request
      const isFilterChangeRequest = options.filterTypes !== undefined;
      if (
        isGradebookFetching &&
        !options.forceGradebookRefresh &&
        !isFilterChangeRequest
      )
        return;

      if (forceInitialFetchLoadingIndicator) {
        dispatch({
          type: actions.GET_GRADEBOOK_REQUEST,
          payload: {
            isGradebookFetching: true,
            isGradebookReFetching: false,
          },
        });
      } else if (!isNextPage) {
        dispatch({ type: actions.GET_GRADEBOOK_REQUEST, payload: {} });
      }

      options.search = options.search || searchPhrase;
      options.searchType = options.searchType || searchType;
      options.exceptionGroups = options.exceptionGroups || exceptionGroups;
      options.filterTypes = options.filterTypes || filterTypes;
      options.page = options.page || currentPage;
      options.searchDateFrom = searchDateFrom;
      options.searchDateTo = searchDateTo;
      options.courseId = courseId;

      let [user, exceptions, instructorHasLinkedIClicker, settingsResponse] =
        await Promise.all([
          lmsService.getCurrentUserAndPermissions(courseId),
          exceptionsService.getCourseExceptions(courseId),
          iClickerService.hasInstructorLinked(courseId),
          lmsService.getGradebookSettings(courseId),
        ]);

      let count;
      let limit;
      let page;
      let pages;
      let filteredStudents = [];
      let userIds = [];

      const initialLimit = parseInt(
        _.get(options, 'limit', DEFAULT_PAGE_SIZE),
        0,
      );
      const initialPage = _.get(options, 'page', 1);
      const loadSingleStudentFromId = _.get(
        options,
        'loadSingleStudentFromId',
        '',
      );
      const studentId = _.get(user, 'user_id', []);
      const permissions = _.get(user, 'permissions', {});
      const canReadClassGrades = _.get(permissions, 'course.grades.class.read');
      const settings = _.get(settingsResponse, 'settings', []);

      if (canReadClassGrades) {
        // If instructor or admin, get the entire roster
        roster = await lmsService.getCourseRoster(courseId);

        // If instructor or admin, need to paginate and filter gradebook data
        if (
          (isAForeignKey(roster, options) && !_.get(options, 'sortByGrade')) ||
          loadSingleStudentFromId
        ) {
          options.limit = roster.length; // if is a foreignKey is required load all the students data
          options.page = 1; // is required reset the page if want to check everything to prevent errors on paginate function
        }

        ({ count, limit, page, pages, filteredStudents } =
          await getFilteredStudents(roster, options));
        userIds = loadSingleStudentFromId
          ? [loadSingleStudentFromId]
          : filteredStudents.map((student) => student.studentId);
      } else {
        userIds = [studentId];
        filteredStudents = [{ studentId }];
        roster = [{ studentId }];
      }

      // Get all gradebook data
      const [gradebookResponse, commentsResponse] = await Promise.all([
        lmsService.getGradebook(courseId, { userIds }),
        lmsService.getGradebookComments(courseId, { userIds }),
      ]);

      // Grab assignments
      let assignments = _.get(gradebookResponse, 'assignments', []);

      if (canReadClassGrades) {
        const { studentSortKey } = getSortProperties(options);
        const sortKeyIsAssignment = _.some(
          assignments,
          (a) => a.id === studentSortKey && isNormalGradedAssignment(a),
        );
        if (sortKeyIsAssignment) {
          options.sortByGrade = true;
          ({ count, limit, page, pages, filteredStudents } =
            await getFilteredStudents(roster, options));
        }
      }

      const unfilteredAssignments = assignments;
      assignments = filterAssignments(unfilteredAssignments, options);

      const categories = assignDefaultCategoryOrder(
        _.get(gradebookResponse, 'categories', []),
      );

      const categoryWeights = _.get(gradebookResponse, 'categoryWeights', []);
      const comments = _.get(commentsResponse, 'comments', []);

      roster = _.map(roster, (student) => {
        const iClickerStudentRosterContainsStudent =
          !!gradebookResponse?.iClickerStudentRosterMap?.[student.studentId];
        const isBrokenSyncReefGrades =
          instructorHasLinkedIClicker && !iClickerStudentRosterContainsStudent;
        return {
          ...student,
          isBrokenSyncReefGrades,
        };
      });

      const gradebookData = {
        ...gradebookResponse,
        assignments,
        exceptions,
        settings,
        comments,
        unfilteredAssignments,
      };

      const students = formatGradebook(
        gradebookData,
        filteredStudents,
        !canReadClassGrades,
        instructorHasLinkedIClicker,
      );

      const lmsId = _.get(gradebookResponse, 'lmsId', '');

      const payload = {
        courseId,
        lmsId,
        pages,
        page,
        limit,
        count,
        course: gradebookResponse?.course || {},
        settings,
        students,
        roster,
        categories,
        categoryWeights,
        user,
        permissions,
        hasInstructorLinkedCourse: instructorHasLinkedIClicker,
        exceptions,
        iClickerStudentRosterMap:
          gradebookResponse?.iClickerStudentRosterMap || {},
        assignments: _.map(assignments, (assignment) => ({
          ...assignment,
          isFinalAssignment: isWritingFinalPaper(
            assignment.tool,
            assignment.parent_id,
          ),
          exceptions: _.filter(exceptions, { assignment_id: assignment.id }),
        })),
        unfilteredAssignments: _.map(unfilteredAssignments, (assignment) => ({
          ...assignment,
          isFinalAssignment: isWritingFinalPaper(
            assignment.tool,
            assignment.parent_id,
          ),
          exceptions: _.filter(exceptions, { assignment_id: assignment.id }),
        })),
      };

      // this means sorter key does not belong to Student info
      if (
        isAForeignKey(filteredStudents, options) &&
        !_.get(options, 'sortByGrade')
      ) {
        const sortedStudents = getSortedStudentsByAssignment(
          students,
          categoryWeights,
          settings,
          options,
          categories,
        );
        const sorter = (student) =>
          _.findIndex(sortedStudents, { studentId: student.studentId });
        const matchedStudents = _.sortBy(students, sorter);
        payload.students = paginate(matchedStudents, initialPage, initialLimit);
        payload.limit = initialLimit;
        payload.page = initialPage;
        payload.pages = Math.ceil(count / initialLimit);
      }

      if (isNextPage) {
        dispatch({
          type: actions.GET_GRADEBOOK_NEXT_PAGE_SUCCESS,
          payload,
        });
      } else {
        dispatch({
          type: actions.APP_LOADING,
          payload: false,
        });
        dispatch({
          type: actions.GET_GRADEBOOK_SUCCESS,
          payload,
        });
      }
    } catch (e) {
      dispatch({ type: actions.GET_GRADEBOOK_ERROR });

      dispatch({
        type: actions.APP_ERROR,
        payload: e,
      });
      throw e;
    }
  };

export const getNewStateSortOptions = (
  newSortColumn,
  newSortDirection,
  history = [],
) => {
  const lastKey = _.last(history);
  const studentSortDirection =
    lastKey !== newSortColumn
      ? GRADEBOOK_SORT.DEFAULT_SORT_DIRECTION
      : newSortDirection;

  if (history.length <= 2) {
    return {
      studentSortDirection,
      studentSortKey: newSortColumn,
      history: [...history, newSortColumn],
      sortColumn: newSortColumn,
    };
  }

  const opts = {
    studentSortDirection,
    studentSortKey: newSortColumn,
    sortColumn: newSortColumn,
  };

  const newStateHistory = [...history];

  if (history[1] === newSortColumn && history[2] === newSortColumn) {
    newStateHistory.push(GRADEBOOK_SORT.NAME_SORT_COLUMN);
    // sending studentSortKey as null you can delete that from the URL check the method getSortProperties();
    opts.studentSortKey = null;
    opts.studentSortDirection = GRADEBOOK_SORT.DEFAULT_SORT_DIRECTION;
    opts.sortColumn = GRADEBOOK_SORT.NAME_SORT_COLUMN;
  } else {
    newStateHistory.push(newSortColumn);
  }

  newStateHistory.splice(0, 1);
  opts.history = newStateHistory;
  return opts;
};

/**
 * Assigns default category order to categories if not set.
 * Puts default category first and ungraded category last.
 */
const assignDefaultCategoryOrderForInstructor = (categories) => {
  const clone = _.cloneDeep(categories);
  const categoriesOrder = clone
    .filter((category) => category.category_order)
    .map((cat) => cat.category_order);
  const defaultCategory = clone.find((category) => category.is_default);
  const ungradedCategory = clone.find(
    (category) => category.is_default_ungraded,
  );

  if (
    defaultCategory &&
    !categoriesOrder.includes(1) &&
    defaultCategory.category_order === null
  ) {
    defaultCategory.category_order = 1;
    categoriesOrder.push(1);
  }

  if (
    ungradedCategory &&
    !categoriesOrder.includes(categories.length) &&
    ungradedCategory.category_order === null
  ) {
    ungradedCategory.category_order = categories.length;
    categoriesOrder.push(categories.length);
  }

  const getIndexAvailable = () => {
    for (let i = 1; i <= categories.length; i++) {
      if (!categoriesOrder.includes(i)) {
        categoriesOrder.push(i);
        return i;
      }
    }
    return categories.length + 1;
  };

  return _.sortBy(
    clone.map((category) => ({
      ...category,
      category_order: !_.isNil(category.category_order)
        ? category.category_order
        : getIndexAvailable(),
    })),
    'category_order',
  );
};

/**
 * New optimized action for instructor gradebook that uses the consolidated server endpoint.
 * Reduces API calls from 7 to 1 and handles pagination/search/sort server-side.
 *
 * @param {string} courseId - The course UUID
 * @param {Object} options - Request options
 * @param {number} [options.page=1] - Page number
 * @param {number} [options.pageSize=25] - Students per page
 * @param {string} [options.search] - Search term for name/email
 * @param {string} [options.sortBy='name'] - Sort field ('name' or 'course_total')
 * @param {string} [options.sortByAssignment] - Assignment UUID to sort by
 * @param {number} [options.sortByCategory] - Category ID to sort by
 * @param {string} [options.sortDirection='asc'] - Sort direction
 * @param {string[]} [options.exceptionGroupIds] - Exception group filter
 * @param {boolean} [options.includeDroppedStudents=false] - Include dropped students
 */
export const getGradebookInstructor =
  (courseId, options = {}) =>
  async (dispatch, getState) => {
    try {
      const {
        gradebook: {
          isGradebookFetching,
          searchPhrase,
          searchType: stateSearchType,
          exceptionGroups,
          page: currentPage,
        },
      } = getState();

      // Prevent double requests (allow forced refresh after grade override save)
      const isFilterChangeRequest = options.filterTypes !== undefined;
      if (
        isGradebookFetching &&
        !options.forceGradebookRefresh &&
        !isFilterChangeRequest
      )
        return;

      dispatch({
        type: actions.GET_INSTRUCTOR_GRADEBOOK_REQUEST,
        payload: {
          isGradebookFetching: true,
          isGradebookReFetching: !options.forceInitialFetchLoadingIndicator,
        },
      });

      // Get filter state from redux
      const {
        gradebook: {
          filterTypes: stateFilterTypes,
          searchDateFrom: stateSearchDateFrom,
          searchDateTo: stateSearchDateTo,
        },
      } = getState();

      // Helper to convert Date objects to ISO strings for the API
      const toISOString = (date) => {
        if (!date) return null;
        if (date instanceof Date) return date.toISOString();
        if (typeof date === 'string') return date;
        return null;
      };

      const dateFrom = toISOString(
        options.searchDateFrom || stateSearchDateFrom,
      );
      const dateTo = toISOString(options.searchDateTo || stateSearchDateTo);

      // Build request options, using stored state values as defaults
      // Only include optional fields if they have actual values
      // Note: Server-side search only applies to student searches (by name/email)
      // Assignment searches are handled client-side (filters assignments, not students)
      // Use currentSearchType from options, falling back to Redux state (like V1 does)
      const effectiveSearchType = options.currentSearchType || stateSearchType;
      const isStudentSearch = effectiveSearchType === SEARCH_TYPES.STUDENT;
      const resolvedPageSize =
        options.pageSize ??
        getInstructorGradebookSessionPageSize(
          options.limit ?? DEFAULT_PAGE_SIZE,
        );
      const requestOptions = {
        page: parseInt(options.page || currentPage || 1, 10),
        pageSize: parseInt(resolvedPageSize, 10),
        // Only pass search to server for student searches
        search: isStudentSearch ? options.search || searchPhrase || '' : '',
        sortBy: options.sortBy || 'name',
        sortDirection: options.sortDirection || 'asc',
        exceptionGroupIds:
          options.exceptionGroupIds ||
          (exceptionGroups || []).map((g) => g.groupId),
        includeDroppedStudents: options.includeDroppedStudents || false,
        filterTypes: options.filterTypes || stateFilterTypes || [],
        clientTimezone: getUserTimezone(),
      };

      // Only add date filters if they have actual values (not null)
      if (dateFrom && dateTo) {
        requestOptions.searchDateFrom = dateFrom;
        requestOptions.searchDateTo = dateTo;
      }

      // Only add optional sort fields if they have actual values
      if (options.sortByAssignment) {
        requestOptions.sortByAssignment = options.sortByAssignment;
      }
      if (options.sortByCategory != null) {
        requestOptions.sortByCategory = parseInt(options.sortByCategory, 10);
      }
      // Pass sortByPoints to sort by points instead of percentage
      if (options.sortByPoints) {
        requestOptions.sortByPoints = true;
      }

      // Fetch user permissions and instructor gradebook in parallel
      const [user, gradebookResponse, instructorHasLinkedIClicker] =
        await Promise.all([
          lmsService.getCurrentUserAndPermissions(courseId),
          lmsService.getInstructorGradebook(courseId, requestOptions),
          iClickerService.hasInstructorLinked(courseId),
        ]);

      const permissions = _.get(user, 'permissions', {});

      // Extract data from consolidated response
      const {
        students: rawStudents,
        pagination,
        assignments,
        categories: rawCategories,
        categoryWeights,
        settings,
        lmsId,
        iClickerStudentRosterMap,
      } = gradebookResponse;

      // Process categories with default ordering
      const categories = assignDefaultCategoryOrderForInstructor(rawCategories);

      // Process exceptions from assignments
      const exceptions = assignments.flatMap(
        (assignment) => assignment.exceptions || [],
      );

      const hasInstructorLinkedCourse = instructorHasLinkedIClicker;

      // Add isBrokenSyncReefGrades to students
      const students = rawStudents.map((student) => {
        const iClickerStudentRosterContainsStudent =
          !!iClickerStudentRosterMap?.[student.studentId];
        const isBrokenSyncReefGrades =
          instructorHasLinkedIClicker && !iClickerStudentRosterContainsStudent;
        return {
          ...student,
          isBrokenSyncReefGrades,
        };
      });

      // Build roster from students
      const roster = students.map((student) => ({
        studentId: student.studentId,
        email: student.email,
        first_name: student.first_name,
        last_name: student.last_name,
        name: student.name,
        thirdPartyUserId: student.thirdPartyUserId,
        isBrokenSyncReefGrades: student.isBrokenSyncReefGrades,
      }));

      // Format assignments with exceptions
      const formattedAssignments = _.map(assignments, (assignment) => ({
        ...assignment,
        isFinalAssignment: isWritingFinalPaper(
          assignment.tool,
          assignment.parent_id,
        ),
        exceptions: _.filter(exceptions, { assignment_id: assignment.id }),
      }));

      // Server already applies resource-type and due-date filters. Only apply
      // client-side assignment name search here.
      const filterOptions = {
        search: options.search || searchPhrase,
        searchType: effectiveSearchType,
      };
      const filteredAssignments = filterAssignments(
        formattedAssignments,
        filterOptions,
      );

      // Create a set of filtered assignment IDs for quick lookup
      const filteredAssignmentIds = new Set(
        filteredAssignments.map((a) => a.id),
      );

      // Filter each student's assignments to match the filtered assignments
      // This ensures student rows only show grades for visible assignments
      const filteredStudents = students.map((student) => ({
        ...student,
        assignments: (student.assignments || []).filter((a) =>
          filteredAssignmentIds.has(a.id),
        ),
      }));

      const {
        gradebook: { unfilteredAssignments: existingUnfilteredAssignments },
      } = getState();
      const hasActiveResourceTypeFilters =
        (filterOptions.filterTypes || []).length > 0;
      const unfilteredAssignments =
        hasActiveResourceTypeFilters && existingUnfilteredAssignments.length
          ? existingUnfilteredAssignments
          : formattedAssignments;

      const payload = {
        courseId,
        lmsId,
        pages: pagination.totalPages,
        page: pagination.page,
        limit: pagination.pageSize,
        count: pagination.totalCount,
        settings,
        students: filteredStudents, // Students with filtered assignments
        roster,
        categories,
        categoryWeights,
        user,
        permissions,
        hasInstructorLinkedCourse,
        exceptions,
        iClickerStudentRosterMap: iClickerStudentRosterMap || {},
        assignments: filteredAssignments,
        unfilteredAssignments,
      };

      dispatch({
        type: actions.APP_LOADING,
        payload: false,
      });

      dispatch({
        type: actions.GET_INSTRUCTOR_GRADEBOOK_SUCCESS,
        payload,
      });
    } catch (e) {
      dispatch({ type: actions.GET_INSTRUCTOR_GRADEBOOK_ERROR });

      dispatch({
        type: actions.APP_ERROR,
        payload: e,
      });
      throw e;
    }
  };
